import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import SectionLoader from "components/common/SectionLoader";
import { AlertType } from "components/common/Snackbar";

import { GetGroups } from "store/actions/GetGroups";
import { showSnackbar } from "store/actions/Utility";

import { createGroup, renameGroup, deleteGroup, moveGroup, assignMembers } from "services/groups";

import { buildTree } from "./buildTree";
import GroupTree from "./GroupTree";
import GroupDialogs from "./GroupDialogs";

import "./Groups.scss";

export default function Groups({ customerId: customerIdProp } = {}) {
  const dispatch = useDispatch();
  const { groups, loadingGroups } = useSelector((state) => state.GetGroups || {});
  const [action, setAction] = useState(null);

  const loggedInUser = useMemo(() => JSON.parse(localStorage.getItem("loggedInUser") || "{}"), []);

  // When rendered as a customer-details tab, the viewed customer's id is passed in.
  // When rendered as a customer admin's own top-level page, fall back to the logged-in user's customer.
  const customerId = customerIdProp || loggedInUser.customerId;

  const reload = () => dispatch(GetGroups({ customerId }));

  useEffect(() => {
    if (customerId) reload();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [customerId]);

  const tree = useMemo(() => buildTree(groups || []), [groups]);

  const handleSubmit = async (payload) => {
    try {
      let res;
      if (payload.kind === "create") res = await createGroup(payload.node.Group_ID, payload.name);
      else if (payload.kind === "rename") res = await renameGroup(payload.node.Group_ID, payload.name);
      else if (payload.kind === "move") res = await moveGroup(payload.node.Group_ID, payload.newParentId);
      else if (payload.kind === "delete") res = await deleteGroup(payload.node.Group_ID);
      else if (payload.kind === "assign") {
        const members = (payload.principalIds || []).map((principalId) => ({
          principalType: payload.principalType,
          principalId,
        }));
        res = await assignMembers(payload.node.Group_ID, members);
      }

      if (res && res.ok) {
        dispatch(showSnackbar({ type: AlertType.success, message: "Saved", dispatch }));
        setAction(null);
        reload();
      } else {
        dispatch(
          showSnackbar({ type: AlertType.error, message: res?.payload?.message || "Action failed", dispatch })
        );
      }
    } catch (e) {
      dispatch(showSnackbar({ type: AlertType.error, message: "Action failed", dispatch }));
    }
  };

  const handleAction = (kind, node) => {
    // Delete opens a confirmation dialog (with consequences guidance) like the other actions;
    // the actual delete request is issued from handleSubmit once the user confirms.
    setAction({ kind, node });
  };

  return (
    <div className="groups-page">
      <div className="groups-page-title">Groups</div>
      {loadingGroups ? (
        <SectionLoader />
      ) : (
        <GroupTree tree={tree} onAction={handleAction} />
      )}
      <GroupDialogs action={action} allGroups={groups || []} onClose={() => setAction(null)} onSubmit={handleSubmit} />
    </div>
  );
}
